import { useEffect, useState, type FormEvent } from 'react';
import type { EvidenceChainEvent } from '@stfors/shared-types';
import { Modal } from './Modal';
import { DateInput } from './DateInput';
import { LookupSelect } from './LookupSelect';
import { ApiError } from '../api/client';
import {
  useUpdateEvidenceEvent,
  type EvidenceEventType,
} from '../api/queries/evidence';
import './EditEvidenceEventModal.css';

interface Props {
  open: boolean;
  event: EvidenceChainEvent;
  onClose: () => void;
}

export function EditEvidenceEventModal({ open, event, onClose }: Props): JSX.Element {
  const update = useUpdateEvidenceEvent();

  const [eventType, setEventType] = useState<EvidenceEventType>('note');
  const [eventAt, setEventAt] = useState<number | null>(null);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [description, setDescription] = useState('');
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setEventType(event.event_type as EvidenceEventType);
    setEventAt(event.event_at);
    setFromLocation(event.from_location ?? '');
    setToLocation(event.to_location ?? '');
    setDescription(event.description ?? '');
    setTopError(null);
    setFieldErrors({});
  }, [open, event]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (eventAt == null) {
      setFieldErrors({ event_at: 'Event date is required' });
      return;
    }

    try {
      await update.mutateAsync({
        evidence_id: event.evidence_id,
        event_id: event.id,
        event_type: eventType,
        event_at: eventAt,
        from_location: fromLocation.trim() || null,
        to_location: toLocation.trim() || null,
        description: description.trim() || null,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don’t have permission to edit chain-of-custody events.");
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setTopError('That event no longer exists.');
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { error?: string; details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const mapped: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) mapped[k] = v[0]!;
          }
          if (Object.keys(mapped).length > 0) {
            setFieldErrors(mapped);
            return;
          }
        }
        setTopError(body?.error ?? 'Please check the fields.');
        return;
      }
      setTopError('Could not save the correction. Try again.');
    }
  };

  const busy = update.isPending;

  return (
    <Modal
      title="Edit Chain-of-Custody Event"
      open={open}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="edit-evidence-event-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save correction'}
          </button>
        </>
      }
    >
      <form id="edit-evidence-event-form" className="evm-form" onSubmit={onSubmit} noValidate>
        <p className="evm-note">
          Corrects this event. The change is recorded in the audit log and the event is marked
          edited — the timeline entry is not deleted or re-ordered.
        </p>

        <div className="evm-row">
          <label className="evm-field">
            <span>Event type</span>
            <LookupSelect
              category="evidence_event_type"
              value={eventType}
              onChange={(v) => setEventType(v as EvidenceEventType)}
              className="form-input"
              disabled={busy}
            />
          </label>
          <label className="evm-field">
            <span>Event date &amp; time</span>
            <DateInput value={eventAt} onChange={setEventAt} withTime disabled={busy} />
            {fieldErrors['event_at'] && <span className="field-error">{fieldErrors['event_at']}</span>}
          </label>
        </div>

        <div className="evm-row">
          <label className="evm-field">
            <span>Transferred from</span>
            <input
              type="text"
              className="form-input"
              value={fromLocation}
              onChange={(e) => setFromLocation(e.target.value)}
              maxLength={500}
              disabled={busy}
              placeholder="e.g. Evidence locker A, Det. Ruiz"
            />
          </label>
          <label className="evm-field">
            <span>Transferred to</span>
            <input
              type="text"
              className="form-input"
              value={toLocation}
              onChange={(e) => setToLocation(e.target.value)}
              maxLength={500}
              disabled={busy}
              placeholder="e.g. Lab bench 3, Examiner J. Qiu"
            />
          </label>
        </div>

        <label className="evm-field evm-field-wide">
          <span>Notes / description</span>
          <textarea
            className="form-input evm-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
          />
        </label>

        {topError && <p className="evm-error-banner" role="alert">{topError}</p>}
      </form>
    </Modal>
  );
}
